import { useEffect, useState } from 'react';
import { Button, Card, Icon, Table, cn, type Column } from '@evogard/ui';
import { format as formatMoney, subtract, sum, type Money } from '@evogard/domain';
import type { FinanceDashboardRowWire } from '@evogard/contract';
import { compact, money } from './money.js';
import { MeasureToggle, valueOf, type Measure } from './MeasureToggle.js';

/**
 * Um corte do painel como ranking (29/10/2026, para a direção): as `limit`
 * maiores em barras horizontais, com a posição na frente, e o resto somado
 * em "Outros". A linha de lacuna — sem endereço, sem consultor — não entra na
 * corrida, mas aparece no fim, em itálico: o dinheiro dela é tão real quanto
 * o das outras.
 *
 * As barras crescem do zero quando os números chegam e quando a chave troca
 * de receita para despesa, uma depois da outra. "Ver tabela" mostra todas as
 * linhas, com o recebido, o pago e o saldo.
 */
export function RankingBars({
  title,
  descricao,
  rows,
  first,
  limit = 5,
  loading,
}: {
  title: string;
  descricao: string;
  rows: readonly FinanceDashboardRowWire[] | undefined;
  /** O nome da primeira coluna da tabela: Cidade, Consultor. */
  first: string;
  limit?: number;
  loading: boolean;
}) {
  const [measure, setMeasure] = useState<Measure>('receitas');
  const [asTable, setAsTable] = useState(false);
  const list = rows ?? [];

  return (
    <Card
      title={title}
      descricao={descricao}
      actions={
        list.length > 0 && (
          <div className="flex flex-wrap items-center gap-1">
            {!asTable && <MeasureToggle value={measure} onChange={setMeasure} />}
            <Button variant="discreto" size="sm" onClick={() => setAsTable((v) => !v)}>
              <Icon nome={asTable ? 'grafico' : 'grade'} size={16} />
              {asTable ? 'Ver gráfico' : 'Ver tabela'}
            </Button>
          </div>
        )
      }
      bleed={asTable}
    >
      {loading && list.length === 0 ? (
        <RankingSkeleton label={title} />
      ) : list.length === 0 ? (
        <p className="py-8 text-center text-sm text-texto-suave">
          Nenhum lançamento no período.
        </p>
      ) : asTable ? (
        <Table<FinanceDashboardRowWire>
          label={`Receita e despesa ${title.toLowerCase()}`}
          empty="Nenhum lançamento no período."
          items={[...list]}
          rowKey={(r) => r.key}
          columns={columns(first)}
        />
      ) : (
        // a chave remonta as barras: a troca de medida cresce do zero de novo
        <Bars key={measure} rows={list} measure={measure} limit={limit} />
      )}
    </Card>
  );
}

type Line = {
  key: string;
  label: string;
  value: Money;
  /** A posição no ranking; ausente no "Outros" e na lacuna. */
  rank?: number;
  kind: 'ranking' | 'outros' | 'lacuna';
  row?: FinanceDashboardRowWire;
};

function linesOf(
  rows: readonly FinanceDashboardRowWire[],
  measure: Measure,
  limit: number,
): Line[] {
  const ranked = rows
    .filter((r) => !r.gap && valueOf(r, measure) > 0)
    .sort((a, b) => valueOf(b, measure) - valueOf(a, measure));
  const rest = ranked.slice(limit);
  return [
    ...ranked.slice(0, limit).map((r, i): Line => ({
      key: r.key,
      label: r.label,
      value: valueOf(r, measure),
      rank: i + 1,
      kind: 'ranking',
      row: r,
    })),
    ...(rest.length > 0
      ? [
          {
            key: '__outros',
            label: `Outros (${rest.length})`,
            value: sum(...rest.map((r) => valueOf(r, measure))),
            kind: 'outros' as const,
          },
        ]
      : []),
    ...rows
      .filter((r) => r.gap && valueOf(r, measure) > 0)
      .map((r): Line => ({
        key: r.key,
        label: r.label,
        value: valueOf(r, measure),
        kind: 'lacuna',
        row: r,
      })),
  ];
}

function Bars({
  rows,
  measure,
  limit,
}: {
  rows: readonly FinanceDashboardRowWire[];
  measure: Measure;
  limit: number;
}) {
  // nasce em zero e cresce no quadro seguinte, para a transição ter de onde partir
  const [grown, setGrown] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setGrown(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const lines = linesOf(rows, measure, limit);
  if (lines.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-texto-suave">
        {measure === 'receitas' ? 'Nenhuma receita' : 'Nenhuma despesa'} no período.
      </p>
    );
  }
  const max = Math.max(1, ...lines.map((l) => l.value));
  const fill = measure === 'receitas' ? 'bg-fase-azul' : 'bg-fase-laranja';

  return (
    <ul className="flex flex-col gap-1">
      {lines.map((l, i) => (
        <li
          key={l.key}
          title={l.row ? tooltipOf(l.row) : `${l.label}: ${formatMoney(l.value)}`}
          className="grid grid-cols-[minmax(0,10rem)_1fr_5.5rem] items-center gap-x-3 rounded-borda py-1.5 hover:bg-superficie-suave"
        >
          <span
            className={cn(
              'flex min-w-0 items-baseline gap-1.5 text-sm',
              l.kind === 'ranking' ? 'text-texto' : 'italic text-texto-suave',
            )}
          >
            {l.rank !== undefined && (
              <span className="w-5 shrink-0 text-xs font-semibold text-texto-suave tabular-nums">
                {l.rank}º
              </span>
            )}
            <span className="truncate">{l.label}</span>
          </span>
          <span className="h-3.5 overflow-hidden rounded-sm bg-superficie-suave">
            <span
              className={cn(
                'block h-full rounded-sm transition-[width] duration-700 ease-out',
                l.kind === 'outros' ? 'bg-fase-cinza' : fill,
              )}
              style={{
                width: grown ? `${Math.max(1.5, (l.value / max) * 100)}%` : '0%',
                transitionDelay: `${i * 70}ms`,
              }}
            />
          </span>
          <span className="text-right text-sm text-texto tabular-nums">
            {compact(l.value)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/* As larguras do esqueleto, em % — fixas, para a espera não mudar de forma a cada render. */
const SKELETON_WIDTHS = [92, 74, 61, 47, 35, 22] as const;

/** A espera com a forma do ranking: as barras pulsam em onda, de cima para baixo. */
export function RankingSkeleton({ label }: { label: string }) {
  return (
    <ul
      className="flex flex-col gap-1"
      aria-busy="true"
      aria-label={`Carregando: ${label}`}
    >
      {SKELETON_WIDTHS.map((w, i) => (
        <li
          key={i}
          className="grid grid-cols-[minmax(0,10rem)_1fr_5.5rem] items-center gap-x-3 py-1.5"
          aria-hidden
        >
          <span
            className="h-3 w-24 animate-pulse rounded-sm bg-borda"
            style={{ animationDelay: `${i * 110}ms` }}
          />
          <span
            className="h-3.5 animate-pulse rounded-sm bg-borda"
            style={{ width: `${w}%`, animationDelay: `${i * 110}ms` }}
          />
          <span
            className="ml-auto h-3 w-14 animate-pulse rounded-sm bg-borda"
            style={{ animationDelay: `${i * 110}ms` }}
          />
        </li>
      ))}
    </ul>
  );
}

export function tooltipOf(r: FinanceDashboardRowWire): string {
  return `${r.label}\nReceitas: ${formatMoney(money(r.revenue.amount))} (${formatMoney(money(r.revenue.settled))} recebido)\nDespesas: ${formatMoney(money(r.expense.amount))} (${formatMoney(money(r.expense.settled))} pago)`;
}

export function columns(first: string): Column<FinanceDashboardRowWire>[] {
  return [
    {
      title: first,
      alignment: 'inicio',
      cell: (r) =>
        r.gap ? <span className="text-texto-suave">{r.label}</span> : r.label,
    },
    {
      title: 'Receitas',
      alignment: 'fim',
      cell: (r) => formatMoney(money(r.revenue.amount)),
    },
    {
      title: 'Recebido',
      alignment: 'fim',
      cell: (r) => formatMoney(money(r.revenue.settled)),
    },
    {
      title: 'Despesas',
      alignment: 'fim',
      cell: (r) => formatMoney(money(r.expense.amount)),
    },
    {
      title: 'Pago',
      alignment: 'fim',
      cell: (r) => formatMoney(money(r.expense.settled)),
    },
    {
      title: 'Saldo',
      alignment: 'fim',
      cell: (r) =>
        formatMoney(subtract(money(r.revenue.amount), money(r.expense.amount))),
    },
  ];
}
